import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { buildImageSource, buildImageSourceSet } from '../../utils/buildImageSource';
import { useTranslation } from '../../utils/useTranslation';
import { Skeleton } from '../feedback/Skeleton';
import { IconClock } from '../icon/IconClock';
import { IconDeclinedFilled } from '../icon/IconDeclinedFilled';
import { IconImage } from '../icon/IconImage';
import { IconKebab } from '../icon/IconKebab';
import { IconLink } from '../icon/IconLink';
import { IconButton } from '../input/IconButton';
import { Box, BoxProps } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Stack } from '../layout/Stack';
import { Image } from '../media/Image';
import { MediaCover } from '../media/MediaCover';
import { Menu, MenuItem } from '../navigation/Menu';
import { Typography } from '../typography/Typography';
import { CollectionStatus, CollectionUI } from './types';

const CollectionCardRoot = styled(Flex).attrs({
    flexGrow: '1',
    flexBasis: { xs: 'calc(50% - 8px)', md: 'calc(100% / 3 - 16px)' },
    maxWidth: { xs: '50%', md: 'calc(100% / 3)' },
    textDecoration: 'none',
})``;

interface CollectionCardProps {
    collection: CollectionUI;
    onRemove: VoidFunction;
    onEdit: VoidFunction;
    onShare: VoidFunction;
    onMove: VoidFunction;
}

const CollectionMediaPreview = (props: { collection: CollectionUI }) => {
    const { collection } = props;
    const { previewItems } = collection;

    if (collection.defaultCollection) {
        return (
            <MediaCover
                width="100%"
                ratio={1}
                borderRadius={1}
                border={{ xs: '1px solid var(--background-secondary)', sm: 'none' }}
            >
                <Stack
                    backgroundColor="var(--background-secondary)"
                    direction="row"
                    flexWrap="wrap"
                    spacing={1 / 8}
                    count={4}
                    renderItem={(index) => {
                        const preview = previewItems[index];

                        return (
                            <Flex
                                key={index}
                                width="50%"
                                height="50%"
                                flexBasis="calc(50% - 0.5px)"
                                alignItems="center"
                                justifyContent="center"
                                backgroundColor="var(--background-primary)"
                            >
                                {preview?.type === 'image' && (
                                    <Image
                                        src={buildImageSource(preview.media, 'grid')}
                                        srcSet={buildImageSourceSet(preview.media, 'grid')}
                                    />
                                )}
                                {preview?.type === 'video' && (
                                    <Image src={preview.media[0]?.firstFrameUrl} />
                                )}
                                {!preview && (
                                    <IconImage fontSize="30px" fill="var(--color-primary)" />
                                )}
                            </Flex>
                        );
                    }}
                />
            </MediaCover>
        );
    }

    const preview = previewItems[0];

    return (
        <MediaCover
            width="100%"
            borderRadius={1}
            ratio={1}
            backgroundColor="var(--background-primary)"
            border={{ xs: '1px solid var(--background-secondary)', sm: 'none' }}
        >
            {preview?.type === 'image' && (
                <Image
                    src={buildImageSource(preview.media, 'grid')}
                    srcSet={buildImageSourceSet(preview.media, 'grid')}
                />
            )}
            {preview?.type === 'video' && <Image src={preview.media[0]?.firstFrameUrl} />}
            {!preview && (
                <Flex alignItems="center" justifyContent="center" width="60px" height="60px">
                    <IconImage fontSize="60px" fill="var(--color-primary)" />
                </Flex>
            )}
        </MediaCover>
    );
};

interface StatusProps extends BoxProps {
    status: CollectionStatus;
    isPublic: boolean;
}

const Status = (props: StatusProps) => {
    const { status, isPublic } = props;
    if (
        status !== CollectionStatus.DECLINED &&
        status !== CollectionStatus.MODERATION &&
        !(status === CollectionStatus.PUBLISHED && isPublic)
    ) {
        return <></>;
    }

    return (
        <IconButton
            variant="filled"
            shape="rect"
            size="xs"
            backgroundColor={
                status === CollectionStatus.DECLINED ? 'rgba(255, 91, 91, 1)' : undefined
            }
        >
            {status === CollectionStatus.MODERATION && <IconClock fontSize={16} />}
            {status === CollectionStatus.DECLINED && <IconDeclinedFilled fontSize={16} />}
            {status === CollectionStatus.PUBLISHED && isPublic && <IconLink fontSize={16} />}
        </IconButton>
    );
};

export const CollectionCard = (props: CollectionCardProps) => {
    const { collection, onRemove, onEdit, onShare, onMove } = props;
    const t = useTranslation();

    return (
        <CollectionCardRoot
            as={collection.numberOfPosts ? Link : Flex}
            to={collection.url}
            onClick={onMove}
        >
            <Box position="relative" width="100%">
                <CollectionMediaPreview collection={collection} />
                {!collection.defaultCollection && (
                    <>
                        <Menu
                            trigger={
                                <Box position="absolute" right={1} top={1}>
                                    <IconButton variant="filled" shape="rect" size="xs">
                                        <IconKebab fontSize={16} fill="#FFF" />
                                    </IconButton>
                                </Box>
                            }
                        >
                            <MenuItem onClick={onEdit}>
                                <Typography noWrap>{t('Редактировать')}</Typography>
                            </MenuItem>
                            {collection.isPublic ? (
                                <MenuItem onClick={onShare}>
                                    <Typography noWrap>{t('Поделиться')}</Typography>
                                </MenuItem>
                            ) : undefined}
                            <MenuItem onClick={onRemove}>
                                <Typography color="error" noWrap>
                                    {t('Удалить')}
                                </Typography>
                            </MenuItem>
                        </Menu>
                        <Box position="absolute" left={1} top={1}>
                            <Status status={collection.status} isPublic={collection.isPublic} />
                        </Box>
                    </>
                )}
            </Box>
            <Typography py={1} variant="body" weight="medium">
                {collection.displayName}
            </Typography>
        </CollectionCardRoot>
    );
};

export const CollectionCardSkeleton = () => (
    <CollectionCardRoot>
        <Skeleton variant="rect" width="100%" borderRadius={1.5} ratio={1} />
        <Box py={1}>
            <Skeleton variant="text" width="30%" height={20} />
        </Box>
    </CollectionCardRoot>
);
